import { useState, useId } from "react";
import { Link } from "wouter";
import { ChevronRight, Phone, ArrowRight, CheckCircle, Star, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { SITE_PHONE, services } from "@/lib/siteData";
import { toast } from "sonner";

// ─── Breadcrumb ───────────────────────────────────────────────────────────────
interface BreadcrumbItem {
  label: string;
  href?: string;
}
interface BreadcrumbProps {
  items: BreadcrumbItem[];
}
export function Breadcrumb({ items }: BreadcrumbProps) {
  const allItems = [{ label: "Home", href: "/" }, ...items];
  return (
    <nav aria-label="Breadcrumb" className="py-3">
      <ol
        className="flex flex-wrap items-center gap-1 text-sm"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {allItems.map((item, i) => (
          <li
            key={i}
            className="flex items-center gap-1"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />}
            {item.href && i < allItems.length - 1 ? (
              <Link
                href={item.href}
                className="text-gray-500 hover:text-gold transition-colors"
                itemProp="item"
              >
                <span itemProp="name">{item.label}</span>
              </Link>
            ) : (
              <span className="text-navy font-medium" itemProp="name">{item.label}</span>
            )}
            <meta itemProp="position" content={String(i + 1)} />
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ─── Lead Form ────────────────────────────────────────────────────────────────
interface LeadFormProps {
  serviceDefault?: string;
  cityDefault?: string;
  variant?: "light" | "dark";
  title?: string;
  subtitle?: string;
  showMessage?: boolean;
  /** Controls the heading level for the form title. Use 2 when the form is the first heading after H1. Default: 3 */
  headingLevel?: 2 | 3;
}
export function LeadForm({
  serviceDefault = "",
  cityDefault = "",
  variant = "light",
  title = "Get Your Free SEO Audit",
  subtitle = "Discover exactly what's holding your rankings back — no obligation, no pressure.",
  showMessage = true,
  headingLevel = 3,
}: LeadFormProps) {
  const TitleTag = `h${headingLevel}` as "h2" | "h3";
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    serviceInterest: serviceDefault,
    city: cityDefault,
    message: "",
  });

  const submitLead = trpc.leads.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      toast.success("We received your request! Expect a call within 24 hours.");
    },
    onError: (err) => {
      toast.error(err.message || "Something went wrong. Please try again.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    submitLead.mutate({ ...form, source: "website-form" });
  };

  // useId() ensures each LeadForm instance has unique IDs — prevents duplicate-ID accessibility violations
  const uid = useId();
  const ids = {
    name: `${uid}-name`,
    email: `${uid}-email`,
    phone: `${uid}-phone`,
    city: `${uid}-city`,
    service: `${uid}-service`,
    message: `${uid}-message`,
  };

  const isDark = variant === "dark";
  const inputClass = `w-full px-4 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-gold transition-all ${
    isDark
      ? "bg-white/10 border-white/20 text-white placeholder-white/50 focus:bg-white/15"
      : "bg-white border-gray-200 text-navy placeholder-gray-400"
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? "text-white/80" : "text-gray-700"}`;

  if (submitted) {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDark ? "bg-white/10" : "bg-green-50 border border-green-200"}`}>
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" aria-hidden="true" />
        <h3 className={`font-display text-xl font-bold mb-2 ${isDark ? "text-white" : "text-navy"}`}>
          We'll Be In Touch Shortly!
        </h3>
        <p className={`text-sm ${isDark ? "text-white/70" : "text-gray-600"}`}>
          Thank you for reaching out. A Local Ranking Coach specialist will contact you within 24 hours to discuss your free SEO audit.
        </p>
        <a
          href={`tel:${SITE_PHONE}`}
          className="inline-flex items-center gap-2 mt-4 text-gold font-semibold text-sm hover:text-gold-dark transition-colors"
        >
          <Phone className="w-4 h-4" aria-hidden="true" />
          Call us now: {SITE_PHONE}
        </a>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-6 md:p-8 ${isDark ? "bg-white/5 border border-white/10" : "bg-white shadow-lg border border-gray-100"}`}>
      {title && (
        <div className="mb-6">
          <TitleTag className={`font-display text-xl font-bold mb-1 ${isDark ? "text-white" : "text-navy"}`}>{title}</TitleTag>
          {subtitle && <p className={`text-sm ${isDark ? "text-white/70" : "text-gray-600"}`}>{subtitle}</p>}
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate aria-label="Lead capture form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={ids.name} className={labelClass}>Full Name *</label>
            <input
              id={ids.name}
              type="text"
              required
              autoComplete="name"
              placeholder="John Smith"
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              aria-required="true"
            />
          </div>
          <div>
            <label htmlFor={ids.email} className={labelClass}>Email Address *</label>
            <input
              id={ids.email}
              type="email"
              required
              autoComplete="email"
              placeholder="john@company.com"
              className={inputClass}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              aria-required="true"
            />
          </div>
          <div>
            <label htmlFor={ids.phone} className={labelClass}>Phone Number</label>
            <input
              id={ids.phone}
              type="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              className={inputClass}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor={ids.city} className={labelClass}>Your City</label>
            <input
              id={ids.city}
              type="text"
              autoComplete="address-level2"
              placeholder="Orlando, FL"
              className={inputClass}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={ids.service} className={labelClass}>Service Interest</label>
            <select
              id={ids.service}
              className={inputClass}
              value={form.serviceInterest}
              onChange={(e) => setForm({ ...form, serviceInterest: e.target.value })}
            >
              <option value="">Select a service...</option>
              {services.map((s) => (
                <option key={s.slug} value={s.title}>{s.title}</option>
              ))}
              <option value="Not sure">Not sure — need consultation</option>
            </select>
          </div>
          {showMessage && (
          <div className="sm:col-span-2">
            <label htmlFor={ids.message} className={labelClass}>Tell us about your business</label>
            <textarea
              id={ids.message}
              rows={3}
              placeholder="Brief description of your business and SEO goals..."
              className={`${inputClass} resize-none`}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          )}
        </div>
        <button
          type="submit"
          disabled={submitLead.isPending}
          className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-3.5 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-all duration-200 active:scale-[0.97] disabled:opacity-70 disabled:cursor-not-allowed shadow-gold"
        >
          {submitLead.isPending ? (
            <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending...</>
          ) : (
            <><ArrowRight className="w-4 h-4" aria-hidden="true" /> Get My Free Audit</>
          )}
        </button>
        <p className={`text-xs text-center mt-3 ${isDark ? "text-white/60" : "text-gray-500"}`}>
          No spam. No obligation. 100% confidential.
        </p>
      </form>
    </div>
  );
}

// ─── CTA Section ──────────────────────────────────────────────────────────────
interface CTASectionProps {
  title?: string;
  subtitle?: string;
  primaryCTA?: string;
  primaryHref?: string;
}
export function CTASection({
  title = "Ready to Rank #1 in Your Market?",
  subtitle = "Join 500+ businesses that trust Local Ranking Coach to dominate local search. Get your free audit today.",
  primaryCTA = "Get Free SEO Audit",
  primaryHref = "/contact",
}: CTASectionProps) {
  return (
    <section className="gradient-hero py-20 md:py-28" aria-labelledby="cta-heading">
      <div className="container text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/20 border border-gold/30 rounded-full mb-6">
            <Star className="w-3.5 h-3.5 text-gold fill-gold" aria-hidden="true" />
            <span className="text-gold-light text-xs font-semibold uppercase tracking-wider">20+ Years of Proven Results</span>
          </div>
          <h2
            id="cta-heading"
            className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
          >
            {title}
          </h2>
          <p className="text-white/80 text-lg mb-8 leading-relaxed">{subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href={primaryHref}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-all duration-200 active:scale-[0.97] shadow-gold text-base"
            >
              {primaryCTA} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${SITE_PHONE}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-base"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              {SITE_PHONE}
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            {["Free Consultation", "No Long-Term Contracts", "Results Guaranteed", "Nationwide Service"].map((item) => (
              <div key={item} className="flex items-center gap-1.5 text-white/70 text-sm">
                <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Page Hero ────────────────────────────────────────────────────────────────
interface PageHeroProps {
  badge?: string;
  title: string;
  titleHighlight?: string;
  subtitle: string;
  breadcrumbs?: BreadcrumbItem[];
  showForm?: boolean;
  serviceDefault?: string;
  cityDefault?: string;
}
export function PageHero({
  badge,
  title,
  titleHighlight,
  subtitle,
  breadcrumbs,
  showForm = false,
  serviceDefault,
  cityDefault,
}: PageHeroProps) {
  return (
    <section className="gradient-hero pt-32 pb-16 md:pt-36 md:pb-20" aria-labelledby="page-hero-heading">
      <div className="container">
        {breadcrumbs && (
          <div className="mb-4">
            <Breadcrumb items={breadcrumbs} />
          </div>
        )}
        <div className={`grid ${showForm ? "lg:grid-cols-2 gap-12" : "max-w-4xl"} items-center`}>
          <div>
            {badge && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/20 border border-gold/30 rounded-full mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-gold" aria-hidden="true" />
                <span className="text-gold-light text-xs font-semibold uppercase tracking-wider">{badge}</span>
              </div>
            )}
            <h1
              id="page-hero-heading"
              className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4"
            >
              {title}{" "}
              {titleHighlight && (
                <span className="text-gradient-gold">{titleHighlight}</span>
              )}
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-6">{subtitle}</p>
            <div className="flex flex-wrap gap-3">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-all active:scale-[0.97] shadow-gold"
              >
                Get Free Audit <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <a
                href={`tel:${SITE_PHONE}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                Call Now
              </a>
            </div>
          </div>
          {showForm && (
            <div className="animate-fade-in-up animate-delay-200">
              <LeadForm
                variant="dark"
                serviceDefault={serviceDefault}
                cityDefault={cityDefault}
                title="Start Ranking Higher Today"
                subtitle="Fill out the form and we'll contact you within 24 hours."
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────
interface FAQItem {
  question: string;
  answer: string;
}
interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
}
export function FAQSection({ faqs, title = "Frequently Asked Questions" }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="section-padding bg-gray-50" aria-labelledby="faq-heading">
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 id="faq-heading" className="heading-lg text-navy mb-3">{title}</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full" aria-hidden="true" />
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  aria-expanded={openIndex === i}
                  aria-controls={`faq-answer-${i}`}
                >
                  <span className="font-semibold text-navy text-sm md:text-base" itemProp="name">
                    {faq.question}
                  </span>
                  <ChevronRight
                    className={`w-5 h-5 text-gold flex-shrink-0 transition-transform duration-200 ${
                      openIndex === i ? "rotate-90" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {openIndex === i && (
                  <div
                    id={`faq-answer-${i}`}
                    className="px-5 pb-5"
                    itemScope
                    itemType="https://schema.org/Answer"
                    itemProp="acceptedAnswer"
                  >
                    <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials Section ─────────────────────────────────────────────────────
interface TestimonialItem {
  name: string;
  company?: string;
  industry?: string;
  city?: string;
  rating: number;
  content: string;
}
interface TestimonialsSectionProps {
  testimonials: TestimonialItem[];
  title?: string;
}
export function TestimonialsSection({
  testimonials,
  title = "What Our Clients Say",
}: TestimonialsSectionProps) {
  return (
    <section className="section-padding bg-white" aria-labelledby="testimonials-heading">
      <div className="container">
        <div className="text-center mb-12">
          <h2 id="testimonials-heading" className="heading-lg text-navy mb-3">{title}</h2>
          <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real results from real businesses. See how Local Ranking Coach has transformed local search visibility for companies across the country.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm card-hover"
              itemScope
              itemType="https://schema.org/Review"
            >
              <div className="flex items-center gap-1 mb-3" role="img" aria-label={`${t.rating} out of 5 stars`}>
                {[1,2,3,4,5].map(s => (
                  <Star
                    key={s}
                    className={`w-4 h-4 ${s <= t.rating ? "text-gold fill-gold" : "text-gray-200"}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <blockquote
                className="text-gray-700 text-sm leading-relaxed mb-4 italic"
                itemProp="reviewBody"
              >
                "{t.content}"
              </blockquote>
              <div itemProp="author" itemScope itemType="https://schema.org/Person">
                <p className="font-bold text-navy text-sm" itemProp="name">{t.name}</p>
                {t.company && <p className="text-gray-500 text-xs">{t.company}</p>}
                {(t.industry || t.city) && (
                  <p className="text-gold-accessible text-xs font-medium mt-0.5">
                    {[t.industry, t.city].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Popular Locations Widget ────────────────────────────────────────────────
const POPULAR_LOCATIONS = [
  { label: "Orlando, FL", href: "/locations/florida/orlando-seo" },
  { label: "Tampa, FL", href: "/locations/florida/tampa-seo" },
  { label: "Miami, FL", href: "/locations/florida/miami-seo" },
  { label: "Atlanta, GA", href: "/locations/georgia/atlanta-seo" },
  { label: "Charlotte, NC", href: "/locations/north-carolina" },
  { label: "Dallas, TX", href: "/locations/texas" },
  { label: "Houston, TX", href: "/locations/texas" },
  { label: "Phoenix, AZ", href: "/locations/arizona/phoenix-seo" },
  { label: "Chicago, IL", href: "/locations/illinois/chicago-seo" },
  { label: "Denver, CO", href: "/locations/colorado/denver-seo" },
  { label: "Los Angeles, CA", href: "/locations/california/los-angeles-seo" },
  { label: "San Diego, CA", href: "/locations/california/san-diego-seo" },
  { label: "Nashville, TN", href: "/locations/tennessee" },
  { label: "Austin, TX", href: "/locations/austin-tx" },
  { label: "Seattle, WA", href: "/locations/washington" },
];

export function PopularLocations({ className = "" }: { className?: string }) {
  return (
    <section className={`py-14 bg-white border-t border-gray-100 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-extrabold text-navy mb-2 text-center">Popular Service Areas</h2>
        <p className="text-gray-500 text-sm text-center mb-8">
          Local SEO services for businesses across the United States.{" "}
          <a href="/locations" className="text-gold hover:underline font-medium">View all locations →</a>
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {POPULAR_LOCATIONS.map((loc) => (
            <a
              key={loc.href + loc.label}
              href={loc.href}
              className="flex items-center justify-center gap-1 bg-gray-50 border border-gray-200 hover:border-gold/40 hover:bg-gold/5 hover:shadow-sm rounded-lg px-2 py-2.5 text-center transition-all group"
            >
              <span className="text-xs font-semibold text-navy group-hover:text-gold transition-colors leading-tight">{loc.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
